import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/fp/isEqual';
import isEmpty from 'lodash/fp/isEmpty';
import noop from 'lodash/fp/noop';
import BaseSelectDropdown, { filterOptions } from './BaseSelectDropdown';
import useClickOutside from '../../useClickOutside';
import useEffectOnce from '../../hooks/useEffectOnce';
import useMergeRefs from '../../hooks/useMergeRefs';
import ClearButton from './ClearButton';
import SelectFilter from './SelectFilter';
import SelectedOption from './SelectedOption';
import WithFeedbackAndAddon from './WithFeedbackAndAddon';
const DEFAULT_FOCUSED_ITEM_INDEX = -1;
const Select = (props) => {
    const { name, id = name, label, options = [], value, onChange = noop, placeholder, isLoading = false, dropup = false, pullRight = false, autoDropDirection = true, bsSize = 'md', disabled = false, tabIndex = 0, hasError = false, useFilter = false, useClear = false, noItemMessage, selectedOptionText, showSelectedItemIcon = false, showUnselectedItemIcons = false, dropdownClassName, btnClassName, className, inputAddon, errorMessage, warningMessage, messageWhiteSpace = 'normal' } = props, remainingProps = __rest(props, ["name", "id", "label", "options", "value", "onChange", "placeholder", "isLoading", "dropup", "pullRight", "autoDropDirection", "bsSize", "disabled", "tabIndex", "hasError", "useFilter", "useClear", "noItemMessage", "selectedOptionText", "showSelectedItemIcon", "showUnselectedItemIcons", "dropdownClassName", "btnClassName", "className", "inputAddon", "errorMessage", "warningMessage", "messageWhiteSpace"]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [itemDOMValues, setItemDOMValues] = useState([]);
    const [focusedItemIndex, setFocusedItemIndex] = useState(DEFAULT_FOCUSED_ITEM_INDEX);
    const [keyboardUsed, setKeyboardUsed] = useState(false);
    const refSelect = useRef();
    const refToggle = useRef(null);
    const ref = useClickOutside(() => closeMenu());
    const mergedSelectRefs = useMergeRefs(refSelect, ref);
    const updateSelectedItem = (selectOptions, updatedValue) => {
        if (updatedValue && !isEmpty(updatedValue)) {
            setSelectedItem(selectOptions.find(item => item.id === updatedValue[0]) || null);
        }
        else if (selectOptions) {
            setSelectedItem(selectOptions.find(item => item.selected) || null);
        }
    };
    useEffectOnce(() => {
        updateSelectedItem(options, value);
    });
    const [previousOptions, setPreviousOptions] = useState(options);
    if (!isEqual(options, previousOptions)) {
        updateSelectedItem(options, value);
        setFilteredOptions(options);
        setPreviousOptions(options);
    }
    const [previousValue, setPreviousValue] = useState(value);
    if (!isEqual(value, previousValue)) {
        updateSelectedItem(options, value);
        setPreviousValue(value);
    }
    const updateDOMValues = (itemDOMValuesToUpdated = []) => {
        setItemDOMValues(itemDOMValuesToUpdated);
    };
    const handleClearSelectedItem = () => {
        setSelectedItem(null);
        setIsFilterActive(false);
        setFilterValue('');
        onChange(undefined);
    };
    const renderToggle = () => {
        const toggleClasses = classNames('dropdown-toggle', 'form-control', 'text-left', btnClassName && btnClassName, bsSize === 'sm' && 'input-sm', bsSize === 'lg' && 'input-lg', disabled && 'disabled');
        const toggleButton = (_jsxs("button", Object.assign({ type: 'button', id: id, name: name, className: toggleClasses, "data-toggle": 'dropdown', tabIndex: tabIndex, "aria-haspopup": 'true', "aria-expanded": isOpen, onClick: onToggle, ref: refToggle }, { children: [useFilter && isOpen && (_jsx(SelectFilter, { isFilterActive: isFilterActive, filterValue: filterValue, onChange: handleFilterChange })), selectedOptionText ? (selectedOptionText) : (_jsx(SelectedOption, { label: label, placeholder: placeholder, selectedItem: selectedItem, options: options, showSelectedItemIcon: showSelectedItemIcon, showUnselectedItemIcons: showUnselectedItemIcons })), _jsx(ClearButton, { showClear: useClear, selectedItem: selectedItem, onClear: handleClearSelectedItem }), _jsx("span", { className: 'caret' })] })));
        if (!inputAddon && !errorMessage && !warningMessage) {
            return toggleButton;
        }
        return (_jsx(WithFeedbackAndAddon, Object.assign({ bsSize: bsSize, inputAddon: inputAddon, errorMessage: errorMessage, warningMessage: warningMessage, messageWhiteSpace: messageWhiteSpace }, { children: toggleButton })));
    };
    const renderDropdownMenu = () => {
        // When an option was already selected, highlight this option by setting the focusedItemIndex accordingly.
        // In case there was nothing preselected, set the focusedItemIndex to the first item if keyboard was used.
        let currentFocusedItemIndex = focusedItemIndex;
        if (selectedItem) {
            currentFocusedItemIndex = options.findIndex(option => {
                return option.id === selectedItem.id;
            });
        }
        else if (keyboardUsed) {
            currentFocusedItemIndex = 0;
        }
        return (_jsx(BaseSelectDropdown, { isOpen: isOpen, isLoading: isLoading, options: filteredOptions, focusedItemIndex: currentFocusedItemIndex, keyboardUsed: keyboardUsed, updateDOMValues: updateDOMValues, onSelect: onOptionChange, onClose: closeMenu, noItemMessage: noItemMessage, pullRight: pullRight, dropup: dropup, autoDropDirection: autoDropDirection, dropdownClassName: dropdownClassName }));
    };
    const handleFilterChange = (event) => {
        event.preventDefault();
        const targetFilterValue = event.currentTarget.value;
        const currentFilteredOptions = filterOptions(itemDOMValues, targetFilterValue, options);
        // highlight the first item of the search result if at least one item was found
        const newFocusedItemIndex = currentFilteredOptions.length > 0 ? 0 : DEFAULT_FOCUSED_ITEM_INDEX;
        setIsFilterActive(true);
        setFilterValue(targetFilterValue);
        setFilteredOptions(currentFilteredOptions);
        setKeyboardUsed(true);
        setFocusedItemIndex(newFocusedItemIndex);
    };
    const onOptionChange = (currentSelectedItem) => {
        setSelectedItem(currentSelectedItem || null);
        setIsFilterActive(false);
        setFilterValue('');
        setFilteredOptions(options);
        onChange(currentSelectedItem);
        closeMenu();
    };
    const onToggle = (event) => {
        // Don't toggle when component is disabled or
        // when filter is active, means entering some filter value
        // in order to avoid closing menu on space but allow to use it for filtering
        if (disabled || isFilterActive) {
            return;
        }
        // using the enter key on the toggle button will trigger a synthetic click event as all buttons are of
        // type submit by default in HTML. In order to differentiate between real click and a synthetic event
        // caused by they keyboard, use the event details. A synthetic event is always 0.
        const isKeyboardUsed = event.detail === 0;
        setIsOpen(!isOpen);
        setKeyboardUsed(isKeyboardUsed);
    };
    const closeMenu = () => {
        var _a;
        if (isOpen) {
            setIsOpen(false);
            setIsFilterActive(false);
            setFilterValue('');
            setFilteredOptions(options);
            setKeyboardUsed(false);
            setFocusedItemIndex(DEFAULT_FOCUSED_ITEM_INDEX);
            (_a = refToggle.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const classes = classNames('select', 'dropdown', isOpen && 'open', hasError && 'has-error', className && className);
    return (_jsxs("div", Object.assign({}, remainingProps, { className: classes, ref: mergedSelectRefs }, { children: [renderToggle(), renderDropdownMenu()] })));
};
export default Select;
